import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

const DISCOUNT_COOKIE_NAME = 'mg_discount_code';
const DISCOUNT_COOKIE_MAX_AGE = 604800;
const DISCOUNT_MAX_LENGTH = 128;

function isThemePreview() {
  return typeof Shopify !== 'undefined' && (Shopify.designMode || Shopify.visualPreviewMode);
}

/**
 * @param {string | null} value
 * @returns {string}
 */
function normalizeDiscountCode(value) {
  if (typeof value !== 'string') return '';
  return value.trim();
}

/**
 * @param {string | null} value
 * @returns {string}
 */
function sanitizeStoredDiscountCode(value) {
  const code = normalizeDiscountCode(value);

  if (!code || code.length > DISCOUNT_MAX_LENGTH || code.includes(',') || /[\u0000-\u001F\u007F]/.test(code)) {
    return '';
  }

  return code;
}

function secureCookieAttribute() {
  return window.location.protocol === 'https:' ? '; Secure' : '';
}

/**
 * @param {string} code
 */
function setStoredDiscountCode(code) {
  document.cookie = `${DISCOUNT_COOKIE_NAME}=${encodeURIComponent(code)}; Max-Age=${DISCOUNT_COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secureCookieAttribute()}`;
}

function clearStoredDiscountCode() {
  document.cookie = `${DISCOUNT_COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Lax${secureCookieAttribute()}`;
}

/**
 * @returns {string}
 */
function getStoredDiscountCode() {
  const cookiePrefix = `${DISCOUNT_COOKIE_NAME}=`;
  const cookie = document.cookie
    .split('; ')
    .find((item) => item.startsWith(cookiePrefix));

  if (!cookie) return '';

  try {
    return sanitizeStoredDiscountCode(decodeURIComponent(cookie.slice(cookiePrefix.length)));
  } catch (error) {
    return '';
  }
}

function captureDiscountFromUrl() {
  if (isThemePreview()) return;

  const discountCode = sanitizeStoredDiscountCode(new URLSearchParams(window.location.search).get('discount'));
  if (discountCode) setStoredDiscountCode(discountCode);
}

captureDiscountFromUrl();

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#fillStoredDiscountCode();
  }

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Handles updates to the discount code.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    await this.#applyDiscountCode(discountCode.value, discountCode, event);
  };

  #fillStoredDiscountCode() {
    if (isThemePreview()) return;

    const discountCode = getStoredDiscountCode();
    const input = this.querySelector('input[name="discount"]');

    if (
      !discountCode ||
      !(input instanceof HTMLInputElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    if (this.#existingDiscounts().includes(discountCode)) {
      clearStoredDiscountCode();
      return;
    }

    this.#setDiscountInputValue(input, discountCode);
    this.#hideDiscountErrors();
    this.#openDiscountDisclosure();
  }

  /**
   * @param {string} discountCodeValue
   * @param {HTMLInputElement} [discountCodeInput]
   * @param {Event} [event]
   * @returns {Promise<'applied' | 'duplicate' | 'invalid' | 'shipping' | 'network_error' | 'unavailable'>}
   */
  async #applyDiscountCode(discountCodeValue, discountCodeInput, event) {
    const discountCode = normalizeDiscountCode(discountCodeValue);
    if (!discountCode || typeof this.dataset.sectionId !== 'string') return 'unavailable';

    const discountMarker = event ? null : cartPerformance.createStartingMarker('discount-update:auto-apply');
    const abortController = this.#createAbortController();

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCode)) return 'duplicate';

      if (discountCodeInput) this.#setDiscountInputValue(discountCodeInput, discountCode);

      this.#hideDiscountErrors();

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCode].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCode && discount.applicable === false;
        })
      ) {
        if (discountCodeInput) this.#setDiscountInputValue(discountCodeInput, '');
        this.#handleDiscountError('discount_code');
        return 'invalid';
      }

      const newHtml = data.sections[this.dataset.sectionId];
      if (typeof newHtml !== 'string') return 'network_error';

      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCode && discount.applicable === true;
          })
        ) {
          this.#handleDiscountError('shipping');
          if (discountCodeInput) this.#setDiscountInputValue(discountCodeInput, '');
          return 'shipping';
        }
      }

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, newHtml);
      if (getStoredDiscountCode() === discountCode) clearStoredDiscountCode();
      return 'applied';
    } catch (error) {
      return 'network_error';
    } finally {
      this.#activeFetch = null;
      if (event) {
        cartPerformance.measureFromEvent('discount-update:user-action', event);
      } else if (discountMarker) {
        cartPerformance.measureFromMarker(discountMarker);
      }
    }
  }

  /**
   * @param {HTMLInputElement} input
   * @param {string} value
   */
  #setDiscountInputValue(input, value) {
    input.value = value;

    if (value) {
      input.setAttribute('value', value);
    } else {
      input.removeAttribute('value');
    }
  }

  #openDiscountDisclosure() {
    const accordion = this.closest('accordion-custom');
    if (accordion instanceof HTMLElement) {
      accordion.setAttribute('open-by-default-on-desktop', '');
      accordion.setAttribute('open-by-default-on-mobile', '');
    }

    const details = this.closest('details');
    if (details instanceof HTMLDetailsElement) {
      details.open = true;
      requestAnimationFrame(() => {
        details.open = true;
      });
    }
  }

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, data.sections[this.dataset.sectionId]);
    } catch (error) {
    } finally {
      this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  #hideDiscountErrors() {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
